import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { trackChatUxEvent } from '../../utils/eventTracker';
import { tableElementToClipboard, writeClipboard } from '../../utils/answerClipboard';
import { downloadTableAsXlsx, isChatTableDownloadEnabled } from '../../utils/tableDownload';
import { MarkdownStreamingContext } from './CodeBlock';

const NUMERIC_CELL_RE = /^[-+−]?\s*(?:₩|\$)?\s*\d[\d,]*(?:\.\d+)?\s*(?:%|점|명|개|원|회|번|분|초|시간|일|주|개월|년|배|쪽|문항|kg|g|mg|m|cm|mm|km|L|mL|℃|°C)?$/;

const childElements = (children) => React.Children.toArray(children).filter(React.isValidElement);

const textOf = (children) => React.Children.toArray(children)
  .map((child) => {
    if (typeof child === 'string' || typeof child === 'number') return String(child);
    if (React.isValidElement(child)) return textOf(child.props.children);
    return '';
  })
  .join('');

const getColumnCount = (children) => {
  const sections = childElements(children);
  const head = sections.find((node) => node.type === 'thead') || sections[0];
  if (!head) return 0;

  const rows = childElements(head.props.children);
  const firstRow = rows.find((node) => node.type === 'tr') || rows[0];
  if (!firstRow) return 0;

  return childElements(firstRow.props.children).length;
};

// 본문 칸의 80% 이상이 숫자(단위 포함)인 열 — 오른쪽 정렬해 자릿수를 맞춘다.
const getNumericColumns = (children, columnCount) => {
  const body = childElements(children).find((node) => node.type === 'tbody');
  if (!body || columnCount === 0) return new Set();
  const counts = Array.from({ length: columnCount }, () => ({ filled: 0, numeric: 0 }));
  childElements(body.props.children).forEach((row) => {
    childElements(row.props.children).forEach((cell, index) => {
      if (!counts[index]) return;
      const text = textOf(cell.props.children).trim();
      if (!text || text === '-') return;
      counts[index].filled += 1;
      if (NUMERIC_CELL_RE.test(text)) counts[index].numeric += 1;
    });
  });
  return new Set(counts
    .map((count, index) => (count.filled >= 2 && count.numeric / count.filled >= 0.8 ? index : -1))
    .filter((index) => index >= 0));
};

// 머리글·본문 모든 칸이 짧은 열(번호·이름·점수 등) — 띄어쓰기에서 '학생 / A' 처럼 갈라지지 않게 한 줄로 둔다.
const SHORT_CELL_MAX = 10;
// 휴대폰 화면 맞춤 표에서는 이보다 긴 짧은 열까지 한 줄로 두면 긴 글 열이 너무 좁아진다.
const TINY_CELL_MAX = 6;
const getShortColumns = (children, columnCount, maxLength = SHORT_CELL_MAX) => {
  const sections = childElements(children);
  const body = sections.find((node) => node.type === 'tbody');
  if (!body || columnCount === 0) return new Set();
  const longest = Array(columnCount).fill(0);
  sections.forEach((section) => {
    childElements(section.props.children).forEach((row) => {
      childElements(row.props.children).forEach((cell, index) => {
        if (index >= columnCount) return;
        longest[index] = Math.max(longest[index], Array.from(textOf(cell.props.children).trim()).length);
      });
    });
  });
  return new Set(longest
    .map((length, index) => (length > 0 && length <= maxLength ? index : -1))
    .filter((index) => index >= 0));
};

// 휴대폰(480px 이하) 넓은 표의 최소 너비 — 열마다 140px 로 잡으면 짧은 열(요일·반)만 있어도 3열밖에 안 보인다.
// 열의 가장 긴 글자 폭(한글 1em, 영문·숫자 0.6em)만큼 잡되, 긴 글 열은 한 줄 약 14자에서 줄바꿈한다.
const NARROW_FONT_PX = 14;
const NARROW_CELL_PADDING_PX = 24;
const NARROW_EDGE_SLACK_PX = 8;
const NARROW_COL_MIN_PX = 56;
const NARROW_COL_MAX_PX = 224;
const WIDE_CHAR_RE = /[\u1100-\u11ff\u2e80-\u9fff\uac00-\ud7af\uf900-\ufaff\uff00-\uffef]/;

const textWidthEm = (text) => Array.from(text).reduce((sum, ch) => sum + (WIDE_CHAR_RE.test(ch) ? 1 : 0.6), 0);

export const getNarrowTableMinWidth = (children, columnCount) => {
  if (columnCount === 0) return 0;
  const widest = Array(columnCount).fill(0);
  childElements(children).forEach((section) => {
    childElements(section.props.children).forEach((row) => {
      childElements(row.props.children).forEach((cell, index) => {
        if (index >= columnCount) return;
        widest[index] = Math.max(widest[index], textWidthEm(textOf(cell.props.children).trim()));
      });
    });
  });
  const total = widest.reduce((sum, em, index) => {
    const edge = index === 0 || index === columnCount - 1 ? NARROW_EDGE_SLACK_PX : 0;
    const px = Math.min(Math.max(em * NARROW_FONT_PX + NARROW_CELL_PADDING_PX, NARROW_COL_MIN_PX), NARROW_COL_MAX_PX);
    return sum + px + edge;
  }, 0);
  return Math.round(total);
};

// 표시 열 유무와 관계없이 같은 방식으로 감싸 key 가 바뀌지 않게 한다(스트리밍 중 행이 다시 만들어지지 않게).
const markColumns = (children, columnClasses) => {
  const markRow = (row) => {
    if (!React.isValidElement(row)) return row;
    let column = -1;
    return React.cloneElement(row, undefined, React.Children.map(row.props.children, (cell) => {
      if (!React.isValidElement(cell)) return cell;
      column += 1;
      const extra = Object.keys(columnClasses).filter((name) => columnClasses[name].has(column));
      return extra.length
        ? React.cloneElement(cell, { className: [cell.props.className, ...extra].filter(Boolean).join(' ') })
        : cell;
    }));
  };
  return React.Children.map(children, (section) => (
    React.isValidElement(section)
      ? React.cloneElement(section, undefined, React.Children.map(section.props.children, markRow))
      : section
  ));
};

const isChatPage = () => window.location.pathname.startsWith('/chat');

const CopyIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const DownloadIcon = () => (
  <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 3v12" />
    <path d="m7 10 5 5 5-5" />
    <path d="M5 21h14" />
  </svg>
);

const DOWNLOAD_LABELS = { idle: '엑셀', busy: '만드는 중', done: '받았어요', failed: '받기 실패' };

const TableDownloadButton = ({ tableRef }) => {
  const [state, setState] = useState('idle');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleDownload = async () => {
    if (state === 'busy') return;
    setState('busy');
    let next = 'done';
    try {
      const { rows, cols } = await downloadTableAsXlsx(tableRef.current);
      if (isChatPage()) trackChatUxEvent('table_download', { cols, rows });
    } catch (error) {
      console.error('표 엑셀 받기 실패:', error);
      next = 'failed';
    }
    setState(next);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState('idle'), 2000);
  };

  return (
    <button
      aria-label="표를 엑셀 파일로 받기"
      className={`em-table-copy em-table-download ${state === 'done' ? 'is-copied' : ''}`}
      disabled={state === 'busy'}
      onClick={handleDownload}
      type="button"
    >
      <DownloadIcon />
      <span>{DOWNLOAD_LABELS[state]}</span>
    </button>
  );
};

// 표 하나만 한글·엑셀에 붙여 넣기·엑셀 파일로 받기. 답변을 쓰는 동안은 숨긴다 — 표가 자라는 중에 버튼이 생겼다 사라지면 깜빡인다.
const TableTools = ({ tableRef, columnCount }) => {
  const [state, setState] = useState('idle');
  const timerRef = useRef(null);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const handleCopy = async () => {
    try {
      const clip = tableElementToClipboard(tableRef.current);
      const format = await writeClipboard(clip);
      setState('copied');
      if (isChatPage()) {
        trackChatUxEvent('message_copy', { kind: 'table', format, responseChars: clip.plain.length, sourceCount: columnCount });
      }
    } catch (error) {
      console.error('표 복사 실패:', error);
      setState('failed');
    }
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setState('idle'), 2000);
  };

  return (
    <div className="em-table-tools">
      <button
        aria-label={state === 'copied' ? '표 복사 완료' : '표 복사'}
        className={`em-table-copy ${state === 'copied' ? 'is-copied' : ''}`}
        onClick={handleCopy}
        type="button"
      >
        <CopyIcon />
        <span>{state === 'copied' ? '복사됨' : state === 'failed' ? '복사 실패' : '표 복사'}</span>
      </button>
      {isChatTableDownloadEnabled() && <TableDownloadButton tableRef={tableRef} />}
    </div>
  );
};

// 휴대폰 채팅에서 이 열 수까지는 최소 너비 없이 화면 폭에 맞춘다(tables.css). 5열부터는 줄바꿈이 너무 깊어져 가로로 민다.
const MOBILE_FIT_MAX_COLUMNS = 4;

const ResponsiveTable = ({ children, className = '', style, ...props }) => {
  const columnCount = getColumnCount(children);
  const isWide = columnCount > 2;
  const fitsMobile = isWide && columnCount <= MOBILE_FIT_MAX_COLUMNS;
  const tableMinWidth = isWide
    ? `${Math.min(Math.max(columnCount * 140, 560), 1400)}px`
    : '100%';
  const narrowMinWidth = isWide ? `${getNarrowTableMinWidth(children, columnCount)}px` : '100%';
  const streaming = useContext(MarkdownStreamingContext);
  const wrapperRef = useRef(null);
  const tableRef = useRef(null);
  const scrollTrackedRef = useRef(false);
  const [scrollState, setScrollState] = useState({ left: false, right: false });

  const updateScrollState = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const left = el.scrollLeft > 2;
    const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 2;
    setScrollState((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);

  useEffect(() => {
    if (!isWide) return undefined;
    updateScrollState();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(wrapperRef.current);
    return () => observer.disconnect();
  }, [isWide, updateScrollState]);

  const handleScroll = (event) => {
    updateScrollState();
    if (scrollTrackedRef.current || event.currentTarget.scrollLeft < 24) return;
    if (!window.location.pathname.startsWith('/chat')) return;
    scrollTrackedRef.current = true;
    trackChatUxEvent('table_hscroll', { sourceCount: columnCount });
  };

  const numericColumns = getNumericColumns(children, columnCount);
  const columnClasses = {
    'is-num': numericColumns,
    'is-short': isWide ? getShortColumns(children, columnCount) : new Set(),
    'is-tiny': fitsMobile ? getShortColumns(children, columnCount, TINY_CELL_MAX) : new Set(),
  };

  return (
    <div
      className={[
        'em-table-shell',
        isWide ? 'em-table-shell--wide' : '',
        fitsMobile ? 'em-table-shell--fit' : '',
        isWide && scrollState.right ? 'can-scroll-right' : '',
        streaming ? '' : 'has-tools',
      ].filter(Boolean).join(' ')}
    >
      <div
        aria-label={`표, ${columnCount}열`}
        className={[
          'table-wrapper',
          isWide ? 'table-wrapper--wide' : 'table-wrapper--compact',
          isWide && scrollState.left ? 'is-scrolled' : '',
        ].filter(Boolean).join(' ')}
        onScroll={isWide ? handleScroll : undefined}
        ref={wrapperRef}
        role="region"
        tabIndex={isWide ? 0 : undefined}
      >
        <table
          {...props}
          className={className}
          ref={tableRef}
          style={{
            ...style,
            '--em-table-min-width': tableMinWidth,
            '--em-table-min-width-narrow': narrowMinWidth,
          }}
        >
          {markColumns(children, columnClasses)}
        </table>
      </div>
      {!streaming && <TableTools columnCount={columnCount} tableRef={tableRef} />}
    </div>
  );
};

export default ResponsiveTable;
